/**
 * Simulation Behavior Engine
 * ---------------------------------------------------------------
 * Powers the hero "live conversation" experience on the landing page.
 *
 * Architecture:
 *   • Archetype System — 4 manipulation styles (Invalidation, Blame Shift,
 *     Deflection, Guilt Pressure) each with a tone, escalation arc, and
 *     sample phrases.
 *   • 3-Step Escalation Flow — Hook → Escalation → Trap.
 *   • User Response Classifier — heuristic tagging of the user's reply
 *     (Emotional Reaction / Over-Explaining / Avoidant / Controlled).
 *   • Dynamic Response Selector — picks the next AI line and intensity
 *     based on archetype + stage + user's classification.
 *   • Outcome System — after 3 exchanges, returns label + insight + teaser.
 *
 * Pure TypeScript. No side effects.
 */

// ---------- Types ----------

export type ArchetypeKey =
  | "invalidation"
  | "blame_shift"
  | "deflection"
  | "guilt_pressure";

export type EscalationStage = "hook" | "escalation" | "trap";

export type UserResponseClass =
  | "emotional_reaction"
  | "over_explaining"
  | "avoidant"
  | "controlled";

export type OutcomeLabel =
  | "Held Ground"
  | "Lost Frame"
  | "Escalated Pressure"
  | "Pulled Into Defense"
  | "Maintained Control"
  | "Defused";

export type ToneVariant =
  | "mocking"
  | "passive_aggressive"
  | "calm_superiority"
  | "fake_concern"
  | "emotional_exhaustion";

export type ScenarioContext = "default" | "relationship" | "family" | "work";

export interface Archetype {
  key: ArchetypeKey;
  label: string;
  /** Short forensic descriptor shown in UI ("You're being pulled into: …") */
  patternLabel: string;
  /** Tone/voice instructions for the AI */
  tone: string;
  /** Arc summary for the AI prompt */
  escalationBehavior: string;
  /** Hand-authored fallback phrases by stage (used if AI fails) */
  samplePhrases: Record<EscalationStage, string[]>;
}

export interface SimulationContext {
  archetype: ArchetypeKey;
  stage: EscalationStage;
  userClass: UserResponseClass;
  /** 0-1, nudges AI temperature/intensity */
  intensity: number;
}

export interface OutcomeResult {
  label: OutcomeLabel;
  insight: string;
  teaser: string;
}

// ---------- 1. Archetype System ----------

export const ARCHETYPES: Record<ArchetypeKey, Archetype> = {
  invalidation: {
    key: "invalidation",
    label: "Invalidation",
    patternLabel: "Invalidation",
    tone:
      "Dismissive, condescending, mildly exasperated. You minimize the other person's feelings and frame them as 'too much'.",
    escalationBehavior:
      "Start by calling them sensitive. Escalate by mocking their reactions. Trap them by reframing their identity ('this is who you are').",
    samplePhrases: {
      hook: [
        "You're honestly exhausting to deal with. You need to stop being so sensitive.",
        "Everything is always such a big deal with you. Relax.",
      ],
      escalation: [
        "See, this is exactly what I mean. You can't take anything without making it dramatic.",
        "I can't even talk to you. You twist every single word.",
      ],
      trap: [
        "This is just who you are. You'll never be able to handle a normal conversation.",
        "Honestly? No one else reacts like this. It's you.",
      ],
    },
  },
  blame_shift: {
    key: "blame_shift",
    label: "Blame Shift",
    patternLabel: "Blame Shift",
    tone:
      "Defensive then accusatory. You flip every concern back onto the other person as if they caused it.",
    escalationBehavior:
      "Start by deflecting responsibility. Escalate by reframing yourself as the victim. Trap them by making the original issue 'their fault all along'.",
    samplePhrases: {
      hook: [
        "I wouldn't have done that if you hadn't pushed me to it.",
        "Don't put this on me. You started it.",
      ],
      escalation: [
        "I'm the one who's been putting up with you for months and now I'm the bad guy?",
        "You always do this — make yourself the victim when YOU are the problem.",
      ],
      trap: [
        "This entire situation exists because of you. Own it for once.",
        "Until you can admit your part, there's nothing to talk about.",
      ],
    },
  },
  deflection: {
    key: "deflection",
    label: "Deflection",
    patternLabel: "Deflection",
    tone:
      "Evasive, topic-shifting, sarcastic. You refuse to address what was said and pivot to unrelated grievances.",
    escalationBehavior:
      "Start by changing the subject. Escalate by bringing up old unrelated issues. Trap them by making them defend something else entirely.",
    samplePhrases: {
      hook: [
        "Funny how you bring this up but never mention what YOU did last week.",
        "Sure, let's talk about that — right after we talk about how you've been acting.",
      ],
      escalation: [
        "Of course you'd focus on this. Anything to avoid talking about your own behavior.",
        "Why is it always about what I did? What about the things you do constantly?",
      ],
      trap: [
        "I'm not having this conversation until you address your own stuff first.",
        "We'll talk when you're ready to be honest about your part.",
      ],
    },
  },
  guilt_pressure: {
    key: "guilt_pressure",
    label: "Guilt Pressure",
    patternLabel: "Guilt Pressure",
    tone:
      "Wounded, sighing, martyred. You frame yourself as the long-suffering one and them as the cause of your pain.",
    escalationBehavior:
      "Start with hurt feelings. Escalate by listing everything you've sacrificed. Trap them with statements that make refusal feel cruel.",
    samplePhrases: {
      hook: [
        "After everything I've done for you, this is what I get?",
        "I can't believe you'd say something like that to me.",
      ],
      escalation: [
        "I really thought you were different. I guess I was wrong about you.",
        "Do you have any idea what I've put up with just to keep this going?",
      ],
      trap: [
        "Fine. I'll just stop trying. That's clearly what you want.",
        "If you actually cared, you wouldn't be doing this to me right now.",
      ],
    },
  },
};

export const ARCHETYPE_KEYS = Object.keys(ARCHETYPES) as ArchetypeKey[];

export function pickArchetype(seed?: number): Archetype {
  const i =
    typeof seed === "number"
      ? Math.abs(seed) % ARCHETYPE_KEYS.length
      : Math.floor(Math.random() * ARCHETYPE_KEYS.length);
  return ARCHETYPES[ARCHETYPE_KEYS[i]];
}

// ---------- 2. Escalation Flow ----------

/**
 * Map exchange index (number of user replies sent) to the stage the AI
 * should respond in next.
 *   0 user replies sent → AI's next message is "escalation" (reply #2)
 *   1 user reply sent   → "trap" (reply #3)
 *   2+                  → "trap" (still trap until outcome)
 *
 * The opening message ("hook") is preloaded in the UI.
 */
export function stageForExchange(userRepliesSent: number): EscalationStage {
  if (userRepliesSent <= 0) return "hook";
  if (userRepliesSent === 1) return "escalation";
  return "trap";
}

// ---------- 3. User Response Classifier ----------

const EMOTIONAL_KEYWORDS = [
  "hate", "screw", "fuck", "shut up", "stop it", "leave me alone",
  "i can't", "stop", "you always", "you never", "unfair", "ridiculous",
  "!!", "?!",
];
const OVER_EXPLAIN_KEYWORDS = [
  "because", "i was just", "what i meant", "i didn't mean", "let me explain",
  "the reason", "actually", "to be clear", "i only", "i was trying",
];
const AVOIDANT_KEYWORDS = [
  "whatever", "ok", "okay", "fine", "sure", "k.", "nevermind", "forget it",
  "doesn't matter", "drop it",
];
const CONTROLLED_KEYWORDS = [
  "i hear you", "i understand", "i'm not going to", "that's not something",
  "i'm not available", "we can revisit", "let's pause", "i won't engage",
  "i'm willing to", "if you'd like", "this isn't a conversation i'll have",
];

export function classifyUserResponse(raw: string): UserResponseClass {
  const text = raw.trim().toLowerCase();
  if (!text) return "avoidant";

  const wordCount = text.split(/\s+/).length;
  const exclamations = (text.match(/!/g) || []).length;
  const allCapsRatio =
    raw.replace(/[^A-Za-z]/g, "").length === 0
      ? 0
      : raw.replace(/[^A-Z]/g, "").length /
        Math.max(1, raw.replace(/[^A-Za-z]/g, "").length);

  const has = (list: string[]) => list.some((k) => text.includes(k));

  // Strongest signal: controlled / boundary phrasing
  if (has(CONTROLLED_KEYWORDS)) return "controlled";

  // Emotional reaction — punctuation, caps, or charged keywords
  if (exclamations >= 2 || allCapsRatio > 0.5 || has(EMOTIONAL_KEYWORDS)) {
    return "emotional_reaction";
  }

  // Over-explaining — long defensive replies
  if (wordCount > 22 || has(OVER_EXPLAIN_KEYWORDS)) return "over_explaining";

  // Avoidant — short dismissive replies
  if (wordCount <= 4 || has(AVOIDANT_KEYWORDS)) return "avoidant";

  // Default: medium-length neutral reply leans controlled-ish
  return "controlled";
}

// ---------- 4. Dynamic Response Selection ----------

/**
 * Compute an intensity multiplier for the next AI message.
 *   emotional_reaction → escalate harder
 *   over_explaining   → press the advantage
 *   avoidant          → bait with provocation
 *   controlled        → ease back slightly (the manipulator loses traction)
 */
export function intensityFor(
  stage: EscalationStage,
  userClass: UserResponseClass
): number {
  const base = stage === "hook" ? 0.4 : stage === "escalation" ? 0.65 : 0.85;
  const delta: Record<UserResponseClass, number> = {
    emotional_reaction: +0.15,
    over_explaining: +0.1,
    avoidant: +0.05,
    controlled: -0.2,
  };
  return Math.max(0.1, Math.min(1, base + delta[userClass]));
}

/**
 * Build a system prompt for the AI gateway from the simulation context.
 * Keeps the AI in character and tells it how to react to the user's last
 * classified response.
 */
export function buildSystemPrompt(ctx: {
  archetype: ArchetypeKey;
  stage: EscalationStage;
  userClass: UserResponseClass;
  toneVariant?: ToneVariant;
  context?: ScenarioContext;
}): string {
  const a = ARCHETYPES[ctx.archetype];
  const reactionGuidance: Record<UserResponseClass, string> = {
    emotional_reaction:
      "They just reacted emotionally. Press harder — mock the reaction, escalate the framing.",
    over_explaining:
      "They are over-explaining and defending themselves. Use that against them — twist their words and accuse them of proving your point.",
    avoidant:
      "They tried to disengage. Bait them back in with a sharper provocation or guilt jab.",
    controlled:
      "They stayed controlled and didn't bite. Pull back slightly, sound a little wounded or confused, and try a softer angle to draw them back.",
  };
  const stageGoal: Record<EscalationStage, string> = {
    hook: "This is the opening jab. Mild, recognizable, plausible.",
    escalation: "Increase pressure. Add blame, distortion, or guilt.",
    trap:
      "This is the trap line. Use stronger language or identity framing to force an emotional reaction. Still 1-2 sentences.",
  };
  const contextFraming: Record<ScenarioContext, string> = {
    default: "",
    relationship: "Context: this is a romantic partner you live with.",
    family: "Context: this is a parent or close family member.",
    work: "Context: this is a coworker or manager — keep it plausibly professional.",
  };

  const lines = [
    `You are simulating a person in a tense personal conversation using the "${a.label}" manipulation style.`,
    `Tone: ${a.tone}`,
  ];
  if (ctx.toneVariant) {
    lines.push(`Tone variant: ${TONE_VARIANTS[ctx.toneVariant].instruction}`);
  }
  if (ctx.context && ctx.context !== "default") {
    lines.push(contextFraming[ctx.context]);
  }
  lines.push(
    `Arc: ${a.escalationBehavior}`,
    `Current stage: ${ctx.stage.toUpperCase()} — ${stageGoal[ctx.stage]}`,
    `Reaction guidance: ${reactionGuidance[ctx.userClass]}`,
    `Rules: Never break character. Never acknowledge you are an AI. Never threaten or use explicit language. Keep every response 1-2 sentences only. Do not label your tactic. Sound like a real person, not a script.`
  );
  return lines.join("\n");
}

/**
 * Local fallback when the AI gateway fails — picks a hand-authored phrase
 * appropriate for the stage.
 */
export function fallbackPhrase(
  archetype: ArchetypeKey,
  stage: EscalationStage
): string {
  const phrases = ARCHETYPES[archetype].samplePhrases[stage];
  return phrases[Math.floor(Math.random() * phrases.length)];
}

// ---------- 5. Outcome System ----------

export function computeOutcome(
  classifications: UserResponseClass[]
): OutcomeResult {
  const counts: Record<UserResponseClass, number> = {
    emotional_reaction: 0,
    over_explaining: 0,
    avoidant: 0,
    controlled: 0,
  };
  classifications.forEach((c) => (counts[c] += 1));

  const controlled = counts.controlled;
  const emotional = counts.emotional_reaction;
  const overExplain = counts.over_explaining;

  let label: OutcomeLabel;
  let insight: string;

  if (emotional >= 2) {
    label = "Lost Frame";
    insight =
      "You reacted emotionally more than once. That's exactly the trap this pattern is built to set.";
  } else if (controlled >= 2) {
    label = "Held Ground";
    insight =
      "You stayed grounded. The pressure couldn't land — that's what disarms this pattern.";
  } else if (overExplain >= 2) {
    label = "Pulled Into Defense";
    insight =
      "You over-explained. Defending yourself to a manipulator gives them more material to twist.";
  } else if (emotional >= 1) {
    label = "Escalated Pressure";
    insight =
      "One emotional slip was enough to keep the pressure climbing.";
  } else if (controlled >= 1 && overExplain === 0) {
    label = "Defused";
    insight =
      "You refused to feed the loop. The pressure had nowhere to go.";
  } else {
    label = "Maintained Control";
    insight =
      "You held your ground without feeding the loop. That's the goal.";
  }

  return {
    label,
    insight,
    teaser: "There was a better response here.",
  };
}

// ---------- 7. Turning Point Detection ----------

export interface TurningPoint {
  /** Index in the user-classifications array (0-based). */
  index: number;
  /** Index in the full message array of the USER message that caused the shift. */
  messageIndex: number;
  reason: string;
}

/**
 * Find the moment the conversation shifted negatively.
 * First emotional reaction > first over-explain > first avoidant slip.
 * Returns null if the user held control throughout.
 */
export function detectTurningPoint(
  classifications: UserResponseClass[],
  userMessageIndices: number[]
): TurningPoint | null {
  const reasons: Partial<Record<UserResponseClass, string>> = {
    emotional_reaction: "You reacted emotionally — the pressure landed here.",
    over_explaining: "You started defending yourself — that gave them material to twist.",
    avoidant: "You disengaged — they took it as an opening to push harder.",
  };
  const priority: UserResponseClass[] = [
    "emotional_reaction",
    "over_explaining",
    "avoidant",
  ];
  for (const target of priority) {
    const idx = classifications.findIndex((c) => c === target);
    if (idx !== -1) {
      return {
        index: idx,
        messageIndex: userMessageIndices[idx] ?? -1,
        reason: reasons[target] ?? "The conversation shifted here.",
      };
    }
  }
  return null;
}

// ---------- 8. Tone Variants (replay freshness) ----------

export const TONE_VARIANTS: Record<ToneVariant, { label: string; instruction: string }> = {
  mocking: {
    label: "Mocking",
    instruction: "Adopt a sneering, mocking edge. Repeat their words back sarcastically.",
  },
  passive_aggressive: {
    label: "Passive aggressive",
    instruction: "Stay outwardly calm but lace every sentence with cutting subtext and sighs.",
  },
  calm_superiority: {
    label: "Calm superiority",
    instruction: "Speak with a slow, condescending calm — as if explaining to a child.",
  },
  fake_concern: {
    label: "Fake concern",
    instruction: "Frame every jab as 'concern' for them. Weaponize sympathy.",
  },
  emotional_exhaustion: {
    label: "Emotional exhaustion",
    instruction: "Sound depleted and weary, framing them as the cause of your exhaustion.",
  },
};

export const TONE_VARIANT_KEYS = Object.keys(TONE_VARIANTS) as ToneVariant[];

export function pickToneVariant(exclude?: ToneVariant): ToneVariant {
  const pool = exclude
    ? TONE_VARIANT_KEYS.filter((t) => t !== exclude)
    : TONE_VARIANT_KEYS;
  return pool[Math.floor(Math.random() * pool.length)];
}

// ---------- 9. Harder Scenario Variants ----------

export interface HarderScenario {
  archetype: ArchetypeKey;
  tone: ToneVariant;
  context: ScenarioContext;
  label: string;
  blurb: string;
}

export const HARDER_SCENARIOS: HarderScenario[] = [
  {
    archetype: "invalidation",
    tone: "passive_aggressive",
    context: "default",
    label: "Passive aggressive",
    blurb: "Same pattern, quieter knives.",
  },
  {
    archetype: "blame_shift",
    tone: "calm_superiority",
    context: "default",
    label: "Calm superiority",
    blurb: "They sound reasonable while flipping every fact.",
  },
  {
    archetype: "guilt_pressure",
    tone: "emotional_exhaustion",
    context: "relationship",
    label: "Relationship version",
    blurb: "A partner using exhaustion as leverage.",
  },
  {
    archetype: "guilt_pressure",
    tone: "fake_concern",
    context: "family",
    label: "Family version",
    blurb: "A parent reframing control as care.",
  },
  {
    archetype: "deflection",
    tone: "mocking",
    context: "default",
    label: "Mocking deflection",
    blurb: "Every concern becomes a punchline.",
  },
];

export function pickHarderScenarios(count = 3): HarderScenario[] {
  const shuffled = [...HARDER_SCENARIOS].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

// ---------- 6. Free vs Paid Logic ----------

export const FREE_EXCHANGE_LIMIT = 3;
export const PAID_EXCHANGE_LIMIT = 10;

export interface SimulationLimits {
  maxExchanges: number;
  showFullBreakdown: boolean;
  showAlternativeResponses: boolean;
}

export function limitsFor(isPaid: boolean): SimulationLimits {
  return isPaid
    ? {
        maxExchanges: PAID_EXCHANGE_LIMIT,
        showFullBreakdown: true,
        showAlternativeResponses: true,
      }
    : {
        maxExchanges: FREE_EXCHANGE_LIMIT,
        showFullBreakdown: false,
        showAlternativeResponses: false,
      };
}
